import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Modal,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import config from '../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const server_URL = config.SERVER_URL;

const Links = () => {
  const [openModal, setOpenModal] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [links, setLinks] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [modalCustomerFilter, setModalCustomerFilter] = useState('');
  const [modalFilteredCampaigns, setModalFilteredCampaigns] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);

  useEffect(() => {
    // Get logged user info
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    
    fetchLinks();
    fetchCampaigns();
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserRole = userData.role_id || null;
      const currentLoggedUserId = userData.id || null;
      
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: currentLoggedUserId,
          role_id: currentUserRole
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list'
      };
      
      // For role 2 users, filter campaigns by their customer_id
      if (userData.role_id === 2 && userData.id) {
        requestBody.customer_id = userData.id;
      }
      
      const response = await fetch(server_URL + '/api/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch campaigns');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        const campaignsData = data.data;
        setCampaigns(campaignsData);
        setAllCampaigns(campaignsData);
        setFilteredCampaigns(campaignsData);
        setModalFilteredCampaigns(campaignsData);
        
        // For role 2 users, auto-set customer filter to logged user
        if (userData.role_id === 2 && userData.id) {
          setModalCustomerFilter(userData.id);
          // Filter modal campaigns by logged user
          const filtered = campaignsData.filter(campaign => campaign.customer_id === userData.id);
          setModalFilteredCampaigns(filtered);
        }
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchLinks = async (filterCampaignId = null, filterCustomerId = null) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null
      };
      
      // Priority: explicit campaign filter > explicit customer filter > role-based filtering
      if (filterCampaignId !== null && filterCampaignId !== '') {
        requestBody.campaign_id = filterCampaignId;
        console.log('Filtering links by campaign_id:', filterCampaignId);
      } else if (filterCustomerId !== null && filterCustomerId !== '') {
        const customerIdValue = String(filterCustomerId) === '0' ? 0 : parseInt(filterCustomerId, 10);
        requestBody.customer_id = customerIdValue;
        console.log('Filtering links by customer_id:', customerIdValue);
      } else if (userData.role_id === 2 && userData.id) {
        // For role 2 users, automatically filter by their own customer_id
        requestBody.customer_id = userData.id;
        console.log('Role 2 user - filtering by own customer_id:', userData.id);
      }
      
      console.log('Request body:', requestBody);
      
      const response = await fetch(server_URL + '/api/links', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch links');
      }

      const data = await response.json();
      console.log('Received links data:', data);
      
      if (data.status === '1' && data.data) {
        setLinks(data.data);
      } else {
        setLinks([]);
      }
    } catch (error) {
      console.error('Error fetching links:', error);
      setLinks([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle customer filter change
  const handleCustomerFilterChange = (customerId) => {
    setCustomerFilter(customerId);
    setCampaignFilter(''); // Reset campaign filter when customer changes
    
    if (customerId === '') {
      // Show all campaigns if no customer selected
      setFilteredCampaigns(allCampaigns);
    } else {
      // Filter campaigns by customer_id
      const customerIdValue = String(customerId) === '0' ? 0 : parseInt(customerId, 10);
      const filtered = allCampaigns.filter(campaign => campaign.customer_id === customerIdValue);
      setFilteredCampaigns(filtered);
      console.log(`Filtered campaigns for customer ${customerIdValue}:`, filtered);
    }
  };

  // Handle campaign filter change
  const handleCampaignFilterChange = (campaignId) => {
    setCampaignFilter(campaignId);
    fetchLinks(campaignId, customerFilter);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setCustomerFilter('');
    setCampaignFilter('');
    setFilteredCampaigns(allCampaigns);
    // For role 2 users, fetchLinks will automatically filter by their own customer_id
    fetchLinks();
  };

  // Handle modal customer filter change
  const handleModalCustomerFilterChange = (customerId) => {
    setModalCustomerFilter(customerId);
    
    if (customerId === '') {
      // Show all campaigns if no customer selected
      setModalFilteredCampaigns(allCampaigns);
    } else {
      // Filter campaigns by customer_id
      const customerIdValue = String(customerId) === '0' ? 0 : parseInt(customerId, 10);
      const filtered = allCampaigns.filter(campaign => campaign.customer_id === customerIdValue);
      setModalFilteredCampaigns(filtered);
      console.log(`Filtered modal campaigns for customer ${customerIdValue}:`, filtered);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!linkName) {
      alert('Please enter a link URL');
      return;
    }

    setIsUploading(true);
    
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'add',
          linkName: linkName,
          campaignId: selectedCampaign || null,
          user_id: userData.id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        })
      });
      
      const data = await response.json();
      if (data.status === '1') {
        setOpenModal(false);
        setLinkName('');
        setSelectedCampaign('');
        fetchLinks();
      } else {
        alert(data.error || 'Failed to add link');
      }
    } catch (error) {
      console.error('Error adding link:', error);
      alert('An error occurred while adding the link');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null
      };
      
      // For role 2 users, filter by their customer_id
      if (userData.role_id === 2 && userData.id) {
        requestBody.customer_id = userData.id;
      }
      
      const response = await fetch(`${server_URL}/api/links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch link details');
      }

      const data = await response.json();
      if (data.status === '1') {
        const link = data.data.find(l => l.id === id);
        if (link) {
          setEditingLink(link);
          setLinkName(link.url);
          setSelectedCampaign(link.campaign_id);
          
          // Set customer filter based on link's customer_id (from campaign)
          if (link.customer_id !== undefined && link.customer_id !== null) {
            setModalCustomerFilter(link.customer_id);
            // Filter campaigns by customer
            const customerIdValue = link.customer_id;
            const filtered = allCampaigns.filter(campaign => campaign.customer_id === customerIdValue);
            setModalFilteredCampaigns(filtered);
          } else {
            setModalCustomerFilter('');
            setModalFilteredCampaigns(allCampaigns);
          }
          
          // For role 2 users, ensure customer filter is set to logged user
          if (userData.role_id === 2 && userData.id) {
            setModalCustomerFilter(userData.id);
            const filtered = allCampaigns.filter(campaign => campaign.customer_id === userData.id);
            setModalFilteredCampaigns(filtered);
          }
          
          setEditModal(true);
        }
      }
    } catch (error) {
      console.error('Error fetching link details:', error);
      alert('Failed to fetch link details');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!linkName) {
      alert('Please enter a link URL');
      return;
    }

    setIsUploading(true);
    
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'update',
          id: editingLink.id,
          url: linkName,
          campaignId: selectedCampaign || null,
          encodedUrl: linkName,
          user_id: userData.id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        })
      });
      
      const data = await response.json();
      if (data.status === '1') {
        setEditModal(false);
        setLinkName('');
        setSelectedCampaign('');
        setEditingLink(null);
        fetchLinks();
      } else {
        alert(data.error || 'Failed to update link');
      }
    } catch (error) {
      console.error('Error updating link:', error);
      alert('An error occurred while updating the link');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    setDeletingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: id,
          user_id: userData.id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete link');
      }

      await fetchLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
      alert('Failed to delete link');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Links
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            // For role 2 users, set customer filter to logged user when opening modal
            if (userRole === 2 && loggedUserId) {
              setModalCustomerFilter(loggedUserId);
              const filtered = allCampaigns.filter(campaign => campaign.customer_id === loggedUserId);
              setModalFilteredCampaigns(filtered);
            } else {
              setModalCustomerFilter('');
              setModalFilteredCampaigns(allCampaigns);
            }
            setOpenModal(true);
          }}
        >
          Add Link
        </Button>
      </Box>
      
      {/* Filter Section - Only show customer filter for role 1 (admin) users */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        alignItems: 'center', 
        mb: 3,
        p: 2,
        bgcolor: '#f5f5f5',
        borderRadius: 1,
        flexWrap: 'wrap'
      }}>
        {userRole === 1 && (
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => handleCustomerFilterChange(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {/* <MenuItem value="0">Common (No Customer)</MenuItem> */}
              {customers.filter(customer => customer.id !== 0).map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.username} ({customer.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {(customerFilter !== '') && (
          <Button 
            variant="outlined"
            color="warning"
            onClick={handleClearFilters}
            sx={{ height: '40px' }}
          >
            Clear Filters
          </Button>
        )}
        
        <Button 
          variant="outlined"
          onClick={() => fetchLinks(campaignFilter, customerFilter)}
          sx={{ height: '40px' }}
        >
          Refresh
        </Button>
      </Box>
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <Typography sx={{ mt: 2 }}>Loading links...</Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Modal
            open={openModal}
            onClose={() => {
              setOpenModal(false);
              setLinkName('');
              setSelectedCampaign('');
              // Reset customer filter based on role
              if (userRole === 2 && loggedUserId) {
                setModalCustomerFilter(loggedUserId);
                const filtered = allCampaigns.filter(campaign => campaign.customer_id === loggedUserId);
                setModalFilteredCampaigns(filtered);
              } else {
                setModalCustomerFilter('');
                setModalFilteredCampaigns(allCampaigns);
              }
            }}
            aria-labelledby="upload-modal-title"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}>
              <Typography id="upload-modal-title" variant="h6" component="h2" mb={3}>
                Add New Link
              </Typography>
              <Stack spacing={3}>
                <TextField
                  label="Link"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  fullWidth
                />
                {userRole === 1 && (
                  <FormControl fullWidth>
                    <InputLabel>Filter by Customer</InputLabel>
                    <Select
                      value={modalCustomerFilter}
                      label="Filter by Customer"
                      onChange={(e) => handleModalCustomerFilterChange(e.target.value)}
                    >
                      <MenuItem value="">All Customers</MenuItem>
                      {/* <MenuItem value="0">Common (No Customer)</MenuItem> */}
                      {customers.filter(customer => customer.id !== 0).map((customer) => (
                        <MenuItem key={customer.id} value={customer.id}>
                          {customer.username} ({customer.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!linkName || isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Adding...
                    </>
                  ) : (
                    'Add Link'
                  )}
                </Button>
              </Stack>
            </Box>
          </Modal>

          <Modal
            open={editModal}
            onClose={() => {
              setEditModal(false);
              setLinkName('');
              setSelectedCampaign('');
              setEditingLink(null);
              setModalCustomerFilter('');
              setModalFilteredCampaigns(allCampaigns);
            }}
            aria-labelledby="edit-modal-title"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}>
              <Typography id="edit-modal-title" variant="h6" component="h2" mb={3}>
                Edit Link
              </Typography>
              <Stack spacing={3}>
                <TextField
                  label="Link"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  fullWidth
                />
                {userRole === 1 && (
                  <FormControl fullWidth>
                    <InputLabel>Filter by Customer</InputLabel>
                    <Select
                      value={modalCustomerFilter}
                      label="Filter by Customer"
                      onChange={(e) => handleModalCustomerFilterChange(e.target.value)}
                    >
                      <MenuItem value="">All Customers</MenuItem>
                      {/* <MenuItem value="0">Common (No Customer)</MenuItem> */}
                      {customers.filter(customer => customer.id !== 0).map((customer) => (
                        <MenuItem key={customer.id} value={customer.id}>
                          {customer.username} ({customer.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button
                  variant="contained"
                  onClick={handleUpdate}
                  disabled={!linkName || isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Link'
                  )}
                </Button>
              </Stack>
            </Box>
          </Modal>
        </>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Link</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                  <Typography variant="h6" color="text.secondary">
                    No links found
                  </Typography>
                  {(customerFilter !== '' || campaignFilter !== '') && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Try clearing the filters or selecting different options
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              links.map((link) => (
                <TableRow 
                  key={link.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box 
                      component="span" 
                      onClick={() => copyToClipboard(link.encoded_url)}
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {link.url}
                      <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}>
                        <svg 
                          aria-hidden="true" 
                          focusable="false" 
                          data-prefix="far" 
                          data-icon="copy" 
                          className="svg-inline--fa fa-copy" 
                          role="img" 
                          viewBox="0 0 448 512" 
                          width="12"
                        >
                          <path fill="currentColor" d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z"></path>
                        </svg>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => handleEdit(link.id)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDelete(link.id)}
                      disabled={deletingId === link.id}
                  >
                    {deletingId === link.id ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

function copyToClipboard(text) {
  // Try modern Clipboard API first
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success('Link copied to clipboard!', {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      })
      .catch((err) => {
        console.error('Failed to copy to clipboard:', err);
        fallbackCopyToClipboard(text);
      });
  } else {
    // Fallback for older browsers or non-HTTPS environments
    fallbackCopyToClipboard(text);
  }
}

function fallbackCopyToClipboard(text) {
  try {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Make it invisible
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    // Execute copy command
    const successful = document.execCommand('copy');
    
    // Clean up
    document.body.removeChild(textArea);
    
    if (successful) {
      toast.success('Link copied to clipboard!', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      toast.error('Failed to copy to clipboard', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  } catch (err) {
    console.error('Fallback copy failed:', err);
    toast.error('Failed to copy to clipboard', {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }
}

export default Links;
